/**
 * Mail-Protokoll — mandantenweite Sicht auf JEDEN Versandversuch (Antoine, 26.08.2026: „Wo können
 * wir das Protokoll aller versendeten E-Mails einsehen? … Falls es einen Bounce gab etc., muss
 * das zu sehen sein." / „Ja, bau die echte Protokoll-Seite. Die muss aber beim Auftrag irgendwo
 * zu sehen sein.").
 *
 * Quelle ist `GET /api/system/emails` (owner/admin, wie `/api/system/errors`) — jede Zeile aus
 * `emails` mit direction=outbound, channel=email, unabhängig davon, ob Cloudflare Email Service
 * oder der Resend-Fallback (integrations.ts#getOfferMailer) sie geschrieben hat. Migration 325
 * schließt dazu eine Lücke: bis dahin scheiterte der Log-Insert des Resend-Fallback-Zweigs an
 * fehlenden Tabellenrechten der Webhook-Rolle und wurde nur verschluckt — genau die Bounce-/
 * Fallback-Zeilen, die diese Seite zeigen soll, fehlten also in der Tabelle.
 *
 * „beim Auftrag sichtbar" (zweite Hälfte des Auftrags): `event-detail.tsx` verlinkt aus der
 * Aktivität-Karte hierher, vorgefiltert auf `?eventId=`. Der Auftrags-Feed selbst zeigt die
 * Mails dieses Vorgangs zwar schon inline (Card L4) — dieser Weg führt zur vollständigen,
 * mandantenweiten Sicht mit Filtern (Status, Volltext), falls der Betreiber vergleichen will.
 *
 * BEWUSSTE VEREINFACHUNG: `emails.eventId` wird nur gesetzt, wenn der Versandpfad ihn kennt (z. B.
 * `offers.ts` beim Angebotsversand). Der `?eventId=`-Filter zeigt deshalb NICHT lückenlos jede
 * Mail, die inhaltlich zu diesem Vorgang gehört — das ist eine bekannte, bestehende Grenze des
 * Datenmodells (s. Kommentar in integrations.ts), keine neue. Migration 326 (27.08.2026) schließt
 * einen Teil davon: `protokolliereVersand()`/`log_outbound_email()` kennen jetzt einen eventId-
 * Parameter, und die meisten Versandstellen (Angebote, Rechnungen, Zahlungslinks) reichen ihn
 * durch — nur wenige Wege ohne Auftragskontext (Einladungen, Anfrage-Bestätigungen, zwei
 * Betriebs-Benachrichtigungen) bleiben bewusst ohne eventId.
 *
 * Auftragssortiert (Umbau 27.08.2026, Antoine: "Die Menüpunkte Zahlungen/Mail-Protokoll müssen
 * auftragssortiert sein. Also ein Auftrag öffnen, dann alle Mails."): ohne aktiven `?eventId=`-
 * Filter gruppiert die Liste nach Auftrag, ein zugeklappter Kopf je Vorgang. Mails ohne
 * Auftragsbezug landen in einer eigenen Gruppe "Ohne Auftrag" am Ende, nicht verschwiegen.
 */
import { Fragment, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { useApi } from "../hooks/useApi";
import { vorgangPfad } from "../lib/pfade";
import { anlassAnzeige, emailStatusMeta, formatDateTime, formatMailEreignisse, type MailEreignis, gruppiereWiederholteVersuche, istMailFehlgeschlagen } from "../ui/format";
import { Card, EmptyState, ErrorNote, Icon, Input, ListenReiter, LoadingRows, MailFehlerHinweis, PageHeader, StatusPill } from "../ui/components";

interface MailProtokollZeile {
  id: string;
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  status: string | null;
  error: string | null;
  messageId: string | null;
  receivedAt: string;
  eventId: string | null;
  eventNumber: string | null;
  /** Handy-Mockup 26.09.2026 (Frage 3): Kunde (Firma vor Person) und Anlass des Vorgangs. */
  customerName?: string | null;
  eventOccasion?: string | null;
  /** Punkt 114: Brevo-Ereignisse (zugestellt/geöffnet/geklickt …). */
  ereignisse?: MailEreignis[];
}

/**
 * Handy-Mockup 26.09.2026: Status-Filter als Reiter statt Auswahlfeld (auch auf dem Desktop). Ein
 * Reiter bündelt mehrere `emails.status`-Werte; die Route nimmt sie kommagetrennt und zählt je
 * Reiter (`zaehler`), unabhängig vom gewählten Reiter.
 */
const STATUS_REITER = [
  { key: "", label: "Alle", status: "" },
  { key: "fehlgeschlagen", label: "Fehlgeschlagen", status: "failed,bounced,complained" },
  { key: "zugestellt", label: "Zugestellt", status: "delivered" },
  { key: "unterwegs", label: "Unterwegs", status: "sent,deferred" },
] as const;
type StatusReiter = (typeof STATUS_REITER)[number]["key"];
type Zaehler = Record<"alle" | "fehlgeschlagen" | "zugestellt" | "unterwegs", number>;

function Zeile({ mail, zeigeAuftrag = true }: { mail: MailProtokollZeile; zeigeAuftrag?: boolean }) {
  const meta = emailStatusMeta(mail.status);
  return (
    <li className="flex flex-col gap-1.5 border-b border-[#F6F0E4] px-[18px] py-3.5 last:border-b-0 dark:border-white/[0.05]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 truncate font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface">
          {mail.subject ?? "(ohne Betreff)"}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {meta && <StatusPill meta={meta} icon={meta.icon} />}
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" title={mail.receivedAt}>
            {formatDateTime(mail.receivedAt)}
          </span>
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        <span className="inline-flex min-w-0 items-center gap-1">
          <Icon name="mail" className="text-[14px]" />
          <span className="truncate">{mail.toAddr ?? "?"}</span>
        </span>
        <span className="text-outline-variant">·</span>
        <span className="min-w-0 truncate">von {mail.fromAddr}</span>
        {zeigeAuftrag && mail.eventNumber && mail.eventId && (
          <>
            <span className="text-outline-variant">·</span>
            <Link to={vorgangPfad(mail.eventId)} className="text-primary hover:underline">
              {mail.eventNumber}
            </Link>
          </>
        )}
      </div>
      {formatMailEreignisse(mail.ereignisse) && (
        <p data-testid={`mail-ereignisse-${mail.id}`} className="mt-1 flex items-center gap-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          <Icon name="timeline" className="text-[14px]" />
          {formatMailEreignisse(mail.ereignisse)}
        </p>
      )}
      {mail.error && <MailFehlerHinweis rawError={mail.error} />}
    </li>
  );
}

/**
 * G6-Verdichtung (28.08.2026, siehe event-detail.tsx): mehrere Versandversuche derselben Mail
 * (gleicher Betreff, kurz hintereinander — z. B. mehrfache Retries nach einem Fehlschlag) zu
 * einer Zeile buendeln, statt jede einzeln aufzulisten. Ohne Buendelung (haeufigster Fall: genau
 * EIN Versuch) bleibt die Darstellung IDENTISCH zu vorher — nur bei mehreren Versuchen kommt
 * eine "X weitere … · Einzeln anzeigen"-Zeile dazu, analog zur Aktivitaets-Karte im Vorgang.
 */
function ZeilenListe({ zeilen, zeigeAuftrag }: { zeilen: MailProtokollZeile[]; zeigeAuftrag: boolean }) {
  const gruppen = useMemo(
    () => gruppiereWiederholteVersuche(zeilen, (m) => m.id, (m) => m.subject),
    [zeilen],
  );
  const [aufgeklappt, setAufgeklappt] = useState<Set<string>>(new Set());
  function toggle(key: string) {
    setAufgeklappt((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  return (
    <>
      {gruppen.map((gruppe) => {
        if (gruppe.extra.length === 0) {
          return <Zeile key={gruppe.primary.id} mail={gruppe.primary} zeigeAuftrag={zeigeAuftrag} />;
        }
        const offen = aufgeklappt.has(gruppe.key);
        const alleFehlgeschlagen = gruppe.extra.every((m) => istMailFehlgeschlagen(m.status));
        return (
          <Fragment key={gruppe.key}>
            {/* Juengster Versuch bleibt die Hauptzeile — daran haengt (in event-detail.tsx) der
                "Erneut senden"-Knopf, der so eindeutig dem aktuellen Stand zugeordnet bleibt. */}
            <Zeile mail={gruppe.primary} zeigeAuftrag={zeigeAuftrag} />
            <li className="border-b border-[#F6F0E4] px-[18px] py-2 last:border-b-0 dark:border-white/[0.05]">
              <button
                type="button"
                onClick={() => toggle(gruppe.key)}
                className="font-label-md text-[11px] font-semibold text-primary hover:underline"
              >
                {offen
                  ? "Einzeln anzeigen ausblenden"
                  : `${gruppe.extra.length} weitere${alleFehlgeschlagen ? " fehlgeschlagene" : ""} Zustellung${gruppe.extra.length === 1 ? "" : "en"} · Einzeln anzeigen`}
              </button>
            </li>
            {offen && gruppe.extra.map((m) => <Zeile key={m.id} mail={m} zeigeAuftrag={zeigeAuftrag} />)}
          </Fragment>
        );
      })}
    </>
  );
}

/** Gruppenschlüssel für Mails ohne Auftragsbezug — kein Auftrag hat je diese "Id". */
const OHNE_AUFTRAG = "__ohne_auftrag__";

export default function MailProtokollPage() {
  const [params, setParams] = useSearchParams();
  const eventId = params.get("eventId");
  // Punkt 115 (Paket 3): Status und Suche in der Adresse — nach „Zurück“ wieder da.
  const [zustand, setzeZustand] = useUrlZustand({ status: "", q: "" });
  const { q } = zustand;
  const reiter = STATUS_REITER.find((r) => r.key === zustand.status) ?? STATUS_REITER[0];
  const setStatus = (status: StatusReiter) => setzeZustand({ status });
  const setQ = (q: string) => setzeZustand({ q });

  const query = new URLSearchParams();
  if (reiter.status) query.set("status", reiter.status);
  if (eventId) query.set("eventId", eventId);
  if (q.trim()) query.set("q", q.trim());
  const qs = query.toString();
  const { data, zusatz, loading, error, reload } = useApi<MailProtokollZeile[], { zaehler?: Zaehler }>(`/api/system/emails${qs ? `?${qs}` : ""}`);
  const zaehler = zusatz?.zaehler;
  const mails = data ?? [];

  /** Auftragssortiert: eine Gruppe je Vorgang (Reihenfolge = erstes Auftauchen, die Route liefert
   *  bereits nach Eingang sortiert), "Ohne Auftrag" immer zuletzt. Ist `?eventId=` schon aktiv
   *  (Deep-Link von der Auftragsseite), gäbe es ohnehin nur EINE Gruppe — dann bleibt die Liste
   *  flach, eine einzelne Gruppen-Überschrift wäre nur Redundanz zum Hinweis darüber. */
  const gruppen = useMemo(() => {
    const map = new Map<string, MailProtokollZeile[]>();
    for (const m of mails) {
      const key = m.eventId ?? OHNE_AUFTRAG;
      const arr = map.get(key);
      if (arr) arr.push(m);
      else map.set(key, [m]);
    }
    const ohneAuftrag = map.get(OHNE_AUFTRAG);
    map.delete(OHNE_AUFTRAG);
    const geordnet = Array.from(map.entries());
    if (ohneAuftrag) geordnet.push([OHNE_AUFTRAG, ohneAuftrag]);
    return geordnet;
  }, [mails]);
  const [manuellAufgeklappt, setManuellAufgeklappt] = useState<Set<string>>(new Set());
  const gefiltert = Boolean(reiter.status || q.trim());
  function toggleGruppe(key: string) {
    setManuellAufgeklappt((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <>
      <PageHeader
        title="Mail-Protokoll"
        subtitle="Jeder Versandversuch — Cloudflare Email Service und der Resend-Fallback gleichermaßen, inklusive Bounce-/Fehlertext."
      />

      {eventId && (
        <div className="mb-3 flex items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          <Icon name="filter_alt" className="text-[15px]" />
          Gefiltert auf einen Vorgang.
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() =>
              setParams(
                (p) => {
                  const next = new URLSearchParams(p);
                  next.delete("eventId");
                  return next;
                },
                { replace: true },
              )
            }
          >
            Filter aufheben
          </button>
        </div>
      )}

      <Card className="mb-4 overflow-hidden">
        <ListenReiter
          label="Mails nach Status"
          reiter={STATUS_REITER.map((r) => ({ key: r.key, label: r.label, anzahl: zaehler?.[r.key || "alle"] ?? 0 }))}
          aktiv={reiter.key}
          onWahl={setStatus}
        />
        <div className="p-4">
          <Input
            type="search"
            className="w-full"
            placeholder="Suche in Empfänger, Absender, Betreff …"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      {error && <ErrorNote message={error} onRetry={reload} />}

      {loading ? (
        <Card>
          <LoadingRows rows={6} />
        </Card>
      ) : !error && mails.length === 0 ? (
        <Card>
          <EmptyState
            icon="outgoing_mail"
            title="Keine Mails gefunden"
            hint={
              eventId || reiter.status || q
                ? "Kein Versandversuch entspricht dieser Filterkombination."
                : "Sobald MAESTRO eine E-Mail verschickt — Angebot, Erinnerung, Rechnung — erscheint der Versuch hier, egal ob er gelang."
            }
          />
        </Card>
      ) : eventId ? (
        // Schon auf einen Vorgang gefiltert (Deep-Link von der Auftragsseite) — es gäbe ohnehin
        // nur eine Gruppe, die flache Liste bleibt hier die klarere Darstellung.
        <Card className="flex flex-col overflow-hidden">
          <ul className="flex flex-col">
            <ZeilenListe zeilen={mails} zeigeAuftrag={true} />
          </ul>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
            {gruppen.map(([key, zeilen]) => {
              const erste = zeilen[0]!;
              const ohneAuftrag = key === OHNE_AUFTRAG;
              // Handy-Mockup 26.09.2026 (Frage 3): Kunde, Anlass und Vorgangsnummer statt nur „Vorgang“,
              // dazu Betreff und Zeit der neuesten Mail (die Route liefert neueste zuerst).
              const titel = ohneAuftrag ? "Ohne Auftrag" : (erste.customerName ?? "Vorgang");
              const unterzeile = ohneAuftrag
                ? "Einladungen, Anfrage-Bestätigungen"
                : [anlassAnzeige(erste.eventOccasion), erste.eventNumber !== titel ? erste.eventNumber : null].filter(Boolean).join(" · ");
              const fehlgeschlagen = zeilen.filter((m) => istMailFehlgeschlagen(m.status)).length;
              const aufgeklappt = gefiltert || manuellAufgeklappt.has(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 px-[18px] py-3.5 text-left transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20"
                    onClick={() => toggleGruppe(key)}
                    aria-expanded={aufgeklappt}
                  >
                    <span className="flex min-w-0 items-start gap-2.5">
                      <Icon name={aufgeklappt ? "expand_more" : "chevron_right"} className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant/60" />
                      <span className="min-w-0">
                        {ohneAuftrag ? (
                          <span className="block font-body-md text-body-md font-semibold text-on-surface-variant">{titel}</span>
                        ) : (
                          <Link
                            to={vorgangPfad(key)}
                            onClick={(e) => e.stopPropagation()}
                            className="block font-body-md text-body-md font-semibold text-on-background hover:text-primary hover:underline dark:text-inverse-on-surface"
                          >
                            {titel}
                          </Link>
                        )}
                        {unterzeile && <span className="block text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{unterzeile}</span>}
                        {!ohneAuftrag && (
                          <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
                            <Icon name="mail" className="shrink-0 text-[14px]" />
                            <span className="truncate">{erste.subject ? `„${erste.subject}“` : "(ohne Betreff)"}</span>
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      {fehlgeschlagen > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-error/10 px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-error">
                          {fehlgeschlagen} fehlgeschlagen
                        </span>
                      )}
                      <span className="font-body-sm text-[11.5px] tabular-nums text-secondary dark:text-secondary-fixed-dim">
                        {!ohneAuftrag && `${formatDateTime(erste.receivedAt)} · `}
                        {zeilen.length} Mail{zeilen.length === 1 ? "" : "s"}
                      </span>
                    </span>
                  </button>
                  {aufgeklappt && (
                    <ul className="flex flex-col border-t border-outline-variant/20 bg-surface-container-low/40 dark:border-outline/20 dark:bg-surface-variant/5">
                      <ZeilenListe zeilen={zeilen} zeigeAuftrag={false} />
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </>
  );
}
